import { extractContingenciesSection } from "./extractContingencies.js";
import { normalizeMarkdownText } from "./normalizeStopLossText.js";

const PAGE_HEADING = /^#{1,3}\s*page\s+\d+\s*$/i;

function itemsToText(items) {
    const lines = [];
    let line = "";
    for (const item of items || []) {
        const chunk = String(item?.str || "");
        if (!chunk) {
            if (item?.hasEOL && line.trim()) {
                lines.push(line.replace(/\s+/g, " ").trim());
                line = "";
            }
            continue;
        }
        if (line && !/\s$/.test(line) && !/^\s/.test(chunk)) line += " ";
        line += chunk;
        if (item.hasEOL) {
            const trimmed = line.replace(/\s+/g, " ").trim();
            if (trimmed) lines.push(trimmed);
            line = "";
        }
    }
    const tail = line.replace(/\s+/g, " ").trim();
    if (tail) lines.push(tail);
    return lines.join("\n");
}

function withoutPageMarkers(text) {
    return String(text || "")
        .split("\n")
        .filter((line) => !PAGE_HEADING.test(line.trim()))
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

async function parseWithProposalGenerator(data) {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const response = await fetch("/proposal/parse-stop-loss", {
        method: "POST",
        headers: { "content-type": "application/pdf" },
        body: bytes,
    });
    const type = response.headers.get("content-type") || "";
    if (!type.includes("json")) throw new Error("Stop-loss parser is not available.");
    const parsed = await response.json();
    if (!response.ok || !parsed?.text) throw new Error(parsed?.error || "Could not read that PDF.");
    return {
        text: String(parsed.text),
        found: Boolean(parsed.found),
        pageCount: Number(parsed.pageCount) || 0,
    };
}

/**
 * Read a stop-loss PDF and return the contingencies section when the
 * heading is present, otherwise the full text.
 * Prefers the same anydoc / LiteParse markdown path as the proposal generator.
 * @param {ArrayBuffer | Uint8Array} data
 */
export async function parseStopLossPdf(data) {
    try {
        return await parseWithProposalGenerator(data);
    } catch (error) {
        const message = String(error?.message || error || "");
        if (!/not available|failed to fetch|networkerror|load failed/i.test(message)) throw error;
    }
    const pdfjs = await import("pdfjs-dist");
    const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    const pdf = await pdfjs.getDocument({ data: data instanceof Uint8Array ? data : new Uint8Array(data) }).promise;
    const pages = [];
    for (let number = 1; number <= pdf.numPages; number += 1) {
        const page = await pdf.getPage(number);
        const content = await page.getTextContent();
        const body = itemsToText(content.items);
        if (body) pages.push(`## Page ${number}\n\n${body}`);
    }
    const fullText = pages.join("\n\n").trim();
    if (fullText.replace(/#+\s*page\s+\d+/gi, "").trim().length < 40) {
        throw new Error("That PDF has no extractable text.");
    }
    const contingencies = extractContingenciesSection(fullText);
    return {
        text: normalizeMarkdownText(withoutPageMarkers(contingencies.text)),
        found: contingencies.found,
        pageCount: pdf.numPages,
    };
}
